// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core'

import type { QdsFocusEventDetail } from '../../helpers'
import {
  invariant,
  isOverflowing,
  pickFocusEventAttributes,
} from '../../helpers'
import type { Size } from '../shared'

export type Destination = 'navigation'
export type ListAction = 'multiselect' | 'radio' | 'reorder'

/**
 *
 *
 * @see https://quartz.se.com/build/components/list-item
 */
@Component({
  tag: 'qds-list-item',
  shadow: { delegatesFocus: false },
  styleUrl: 'list-item.css',
})
export class ListItem implements ComponentInterface {
  /**
   * The destination action that the list item denotes through its rightmost icon.
   */
  @Prop() public readonly destination?: Destination

  /**
   * Prevents the list item from being interacted with: it cannot be selected
   * or focused.
   */
  @Prop() public readonly disabled: boolean = false

  /**
   * Control how to display the bottom divider line.
   *
   * By default, the divider is not visible.
   */
  @Prop() public readonly showDivider: boolean = false

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The URL of the avatar image to display.
   */
  @Prop() public readonly avatarSrc?: string

  /**
   * The URL of the picture to display.
   *
   * This is used to display a picture in the list item.
   * It is not used for the avatar image.
   */
  @Prop() public readonly imageSrc?: string

  /**
   * Text to display for the kicker.
   */
  @Prop() public readonly kicker?: string

  /**
   * The type of list-level action that the list item supports
   */
  @Prop() public readonly listAction?: ListAction

  /**
   * The list items's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Whether or not the list item is selected.
   */
  @Prop({ mutable: true }) public selected?: boolean

  /**
   * Sets the list item to an indeterminate state, only when `list-action="indeterminate"`.
   * This is usually applied to items that represent a "select all/none" behavior when associated
   * items have a mix of selected and unselected states.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public indeterminate?: boolean

  /**
   * The primary text of the list item.
   */
  @Prop() public readonly text!: string

  /**
   * Text to display below the primary text.
   */
  @Prop() public readonly subtitle?: string

  /**
   * Displays a counter badge if a number or string representing a number is used.
   *
   * @webnative
   */
  @Prop() public readonly badge?: number | string

  /**
   * The counter badge's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly badgeDescription?: string

  /**
   * Emitted when the list item loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when a change to the checkbox's state is committed by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  /**
   * Emitted when the list item gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  @AttachInternals() private readonly internals!: ElementInternals

  @Element() private readonly host!: HTMLElement

  @State() private tabIndex?: number

  @State() private textTooltip = false

  @State() private subtitleTooltip = false

  #roText?: ResizeObserver

  #roSubTitle?: ResizeObserver

  #text?: HTMLDivElement

  #subtitle?: HTMLDivElement

  get #computedDestination(): Destination | undefined {
    switch (this.destination) {
      case 'navigation': {
        return this.destination
      }
      default: {
        return undefined
      }
    }
  }

  get #computedIndeterminate(): boolean {
    return this.indeterminate ?? false
  }

  get #computedListAction(): ListAction | undefined {
    switch (this.listAction) {
      case 'reorder':
      case 'radio':
      case 'multiselect': {
        return this.listAction
      }
      default: {
        return undefined
      }
    }
  }

  get #computedSelected(): boolean {
    return this.selected ?? false
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.disabled) {
      event.stopImmediatePropagation()
      return
    }

    // Always prevent default behavior to avoid the internal `<input>`
    // to toggle `checked` state inappropriately.
    //
    // Note that this will need adjustments once we add the `item-action` slot.
    event.preventDefault()

    if (this.listAction === 'multiselect') {
      event.stopImmediatePropagation()
      this.selected = this.#computedIndeterminate
        ? true
        : !this.#computedSelected
      this.changeEmitter.emit()
      this.indeterminate = false
    }

    if (this.listAction === 'radio' && !this.#computedSelected) {
      event.stopImmediatePropagation()
      this.selected = !this.#computedSelected
    }
  }

  @Listen('keydown')
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') this.host.click()
  }

  @Listen('keyup')
  protected onKeyup(event: KeyboardEvent): void {
    if (event.key === ' ') this.host.click()
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex = Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.internals.ariaDisabled = this.disabled.toString()
  }

  @Watch('listAction')
  protected listActionChanged(newValue: ListAction | undefined): void {
    this.internals.ariaChecked =
      (newValue === 'radio' || newValue === 'multiselect') &&
      this.#computedSelected
        ? 'true'
        : 'false'
  }

  @Watch('selected')
  protected selectedChanged(): void {
    if (this.listAction === 'multiselect') {
      this.internals.ariaChecked = this.#computedSelected.toString()
    }
  }

  @Watch('text')
  protected textChanged(): void {
    if (this.#roText) this.#roText.disconnect()

    if (!this.#text || !this.text) return

    invariant(this.#text)

    const div = this.#text
    readTask(() => {
      this.textTooltip = isOverflowing(div)
    })
    this.#roText = new ResizeObserver(([divEntry]) => {
      this.textTooltip = isOverflowing(divEntry.target)
    })
    this.#roText.observe(this.#text)
  }

  @Watch('subtitle')
  protected subtitleChanged(): void {
    if (this.#roSubTitle) this.#roSubTitle.disconnect()

    if (!this.#subtitle || this.subtitle === undefined || this.subtitle === '')
      return

    invariant(this.#subtitle)
    const div = this.#subtitle
    readTask(() => {
      this.subtitleTooltip = isOverflowing(div)
    })
    this.#roSubTitle = new ResizeObserver(([subtitleEntry]) => {
      this.subtitleTooltip = isOverflowing(subtitleEntry.target)
    })
    this.#roSubTitle.observe(this.#subtitle)
  }

  public componentWillLoad(): void {
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
    switch (this.listAction) {
      case 'radio': {
        this.internals.role = 'menuitemradio'
        break
      }
      case 'multiselect': {
        this.internals.role = 'menuitemcheckbox'
        break
      }
      case undefined: {
        this.internals.role = 'option'
        break
      }
      default: {
        this.internals.role = 'listitem'
        break
      }
    }
    this.disabledChanged()
  }

  public componentDidLoad(): void {
    this.textChanged()
    this.subtitleChanged()
  }

  public render() {
    return (
      /* eslint-disable jsx-a11y/label-has-associated-control */
      <Host
        onBlur={this.#onBlur}
        onFocus={this.#onFocus}
        tabIndex={this.tabIndex}
        aria-label={this.text}
      >
        <div
          class={{
            'qds-list-item': true,
            'qds-disabled': this.disabled,
            'qds-selected': this.#computedSelected,
          }}
          data-size={this.#computedSize}
        >
          <div
            class={{
              'qds-indented-content': true,
            }}
          >
            {this.#computedListAction === 'multiselect' && (
              <qds-checkbox
                checked={this.selected}
                class="qds-list-action"
                indeterminate={this.indeterminate}
                size={this.size}
                tabindex={-1}
                text={this.text}
                disabled={this.disabled}
                aria-hidden="true"
                checkboxOnly
              />
            )}

            {this.#computedListAction === 'radio' && (
              <qds-radio
                checked={this.selected}
                class="qds-list-action"
                size={this.size}
                tabindex={-1}
                aria-hidden="true"
                disabled={this.disabled}
              />
            )}

            {this.#computedListAction === 'reorder' && (
              <qds-icon
                aria-hidden="true"
                class="qds-icon qds-list-action"
                library="core"
                name="draggable"
              />
            )}

            {this.imageSrc !== undefined && this.imageSrc !== '' && (
              <img class="qds-picture" src={this.imageSrc} alt="" />
            )}

            {this.iconName !== undefined && this.iconName !== '' && (
              <qds-icon
                aria-hidden="true"
                class="qds-icon qds-main-icon"
                library={this.iconLibrary}
                name={this.iconName}
              />
            )}
            {this.avatarSrc !== undefined && this.avatarSrc !== '' && (
              <img class="qds-avatar" src={this.avatarSrc} alt="" />
            )}

            <label class="qds-texts">
              {this.kicker !== undefined && this.kicker !== '' && (
                <div class="qds-kicker">{this.kicker}</div>
              )}
              <div class="qds-title" ref={this.#divTitleRef}>
                {this.text}
              </div>
              {this.subtitle !== undefined && this.subtitle !== '' && (
                <div class="qds-subtitle" ref={this.#divSubtitleRef}>
                  {this.subtitle}
                </div>
              )}
            </label>

            {this.badge !== undefined && this.badge !== '' && (
              <qds-badge-counter
                size={this.#computedSize}
                strokeRing
                value={this.badge}
                description={this.badgeDescription}
              />
            )}

            {this.#computedDestination !== undefined && (
              <qds-icon
                aria-hidden="true"
                class="qds-icon qds-destination qds-main-icon"
                library="core"
                name={this.#computedDestination}
              />
            )}
          </div>
          {this.showDivider && <qds-divider />}
          {this.textTooltip && (
            <qds-tooltip aria-hidden="true" ref={this.#textTooltipRef}>
              {this.text}
            </qds-tooltip>
          )}
          {this.subtitleTooltip && (
            <qds-tooltip aria-hidden="true" ref={this.#subtitleTooltipRef}>
              {this.subtitle}
            </qds-tooltip>
          )}
        </div>
      </Host>

      /* eslint-enable jsx-a11y/label-has-associated-control */
    )
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #divTitleRef = (div?: HTMLDivElement): void => {
    this.#text = div
  }

  readonly #divSubtitleRef = (div?: HTMLDivElement): void => {
    this.#subtitle = div
  }

  readonly #textTooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#text
  }

  readonly #subtitleTooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#subtitle
  }
}
